
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { createUserByStaff, CreateUserData } from '@/hooks/auth/staffUserManagement';
import { toast } from '@/components/ui/sonner';
import { CheckCircle, Info } from 'lucide-react';

interface StaffCreateUserFormProps {
  onUserCreated: () => void;
}

const StaffCreateUserForm: React.FC<StaffCreateUserFormProps> = ({ onUserCreated }) => {
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    email: '',
    role: 'student'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Submitting user creation form:', formData, 'sendWelcomeEmail:', sendWelcomeEmail);
      const success = await createUserByStaff(formData, sendWelcomeEmail);
      
      if (success) {
        console.log('User created successfully, resetting form');
        setFormData({
          name: '',
          email: '',
          role: 'student'
        });
        onUserCreated();
        toast.success('Student account created successfully! They will appear in the user management table.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof CreateUserData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Create New Student Account</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter full name"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmail"
                checked={sendWelcomeEmail}
                onCheckedChange={(checked) => setSendWelcomeEmail(checked as boolean)}
                disabled={isSubmitting}
              />
              <Label htmlFor="sendEmail" className="text-sm font-normal cursor-pointer">
                Send welcome email with login credentials
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">
              The student will receive their temporary password via email
            </p>
          </div>

          <div className="space-y-2">
            <Label>Account Type</Label>
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm font-medium">Student Account</p>
              <p className="text-sm text-muted-foreground">
                Staff members can only create student accounts. The student will receive an email invitation to set up their password.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Info className="h-4 w-4" />
              What happens next?
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                The student account will be created and automatically approved
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                The student will receive an email invitation to access the platform
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                They can sign in using their email and set up their password
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                You can assign courses to them from the user management table
              </li>
            </ul>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormData({
                name: '',
                email: '',
                role: 'student'
              })}
              disabled={isSubmitting}
            >
              Clear
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating Account...' : 'Create Student Account'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default StaffCreateUserForm;
